import { useEffect, useState } from "react";
import isEmpty from "lodash/isEmpty";
import { t } from "@/i18n-js/instance";
import { useRouterQueryParams } from "@/react/hooks/useRouterQueryParams";
import type { CurrentCommunity } from "@/react/types/CurrentCommunity";
import { useCurrentCommunityContext } from "@circle-react/apps/AuthenticationApp";
import { ForgotPasswordLink } from "@circle-react/components/OnboardingComponents/ForgotPasswordLink";
import { LoginOptions } from "@circle-react/components/OnboardingComponents/LoginOptions";
import { SignupLink } from "@circle-react/components/OnboardingComponents/SignupLink";
import { SignupOptions } from "@circle-react/components/OnboardingComponents/SignupOptions";
import { WebviewAuthenticationLayout } from "@circle-react/components/OnboardingPage/WebviewAuthenticationLayout";
import { authenticationAppRoutes } from "@circle-react/components/OnboardingPage/helpers/routes";
import { OnboardingWrapper } from "@circle-react/components/OnboardingPage/layouts/OnboardingWrapper";
import { PageTitle } from "@circle-react/components/OnboardingPage/layouts/PageTitle";
import { useOnboardingWhiteLabel } from "../hooks/useOnboardingWhiteLabel";
import { SignInForm } from "./SignInForm";

interface PromotionDetails {
  skip_sign_up_funnel?: boolean;
  trial_days?: number;
}
export const SignInPage = () => {
  const pageTitle = t("sign_in");
  const [shouldShowForm, setShouldShowForm] = useState(false);
  const {
    currentCommunity,
    promotion_details,
  }: {
    currentCommunity?: CurrentCommunity;
    promotion_details?: PromotionDetails;
  } = useCurrentCommunityContext();
  let { shouldShowPoweredByCircle } = useOnboardingWhiteLabel(currentCommunity);

  const currentHash = window.location.hash;
  let title = currentCommunity
    ? t("login_to_your_account")
    : t("login_to_your_circle_account");
  let step;
  let subtitle;
  if (promotion_details?.skip_sign_up_funnel) {
    title = t("authentication_app.signup.promotion.title");
    step = t("authentication_app.signup.promotion.step_1");
    subtitle = t("authentication_app.signup.promotion.subtitle");
    shouldShowPoweredByCircle = false;
  }
  const urlParams = useRouterQueryParams();
  const isInReactNativeAuthenticatedWebview =
    !!(window as any).webview?.isComingFromReactNativeAuthenticatedWebview ||
    (window as any).webview?.isInsideMobileWebview;
  const isInReactNativeWebview = !!(window as any).webview
    ?.isComingFromReactNativeWebview;

  useEffect(() => {
    if (isEmpty(currentCommunity)) {
      setShouldShowForm(true);
    } else {
      setShouldShowForm(currentHash === "#email");
    }
  }, [currentHash, currentCommunity]);

  if (isInReactNativeAuthenticatedWebview) {
    return <WebviewAuthenticationLayout />;
  }

  return (
    <OnboardingWrapper
      title={title}
      subtitle={subtitle}
      step={step}
      isNarrow
      withCircleBranding={isEmpty(currentCommunity)}
      shouldShowPoweredByCircle={shouldShowPoweredByCircle}
      isVerticallyCenteredOnMobile
      isRegisteringAsACommunityMember={!isEmpty(currentCommunity)}
      isSignUpOrLogIn
    >
      <PageTitle title={pageTitle} />
      <div>
        {shouldShowForm ? (
          <SignInForm />
        ) : (
          <LoginOptions
            formUrl={authenticationAppRoutes.signIn({ params: urlParams })}
            variant="signin"
          />
        )}
      </div>
      {!isInReactNativeWebview && (
        <div className="mt-8 flex flex-col items-center justify-center">
          <SignupLink />
          <ForgotPasswordLink
            url={authenticationAppRoutes.forgotPassword({
              params: urlParams,
            })}
          />
        </div>
      )}
      <SignupOptions variant="signIn" />
    </OnboardingWrapper>
  );
};

export default SignInPage;
